import React from 'react';
import { AlertCircle, CheckCircle, Clock, ExternalLink, RefreshCw, Settings, Trash2 } from 'lucide-react';
import Button from '../ui/Button';
import { Connection, WooCommerceSyncStatus } from '../../types';
import { connectorPresentation } from './connectorPresentation';

interface InstalledConnectorCardProps {
  connection: Connection;
  label: string;
  /** Catalog sync belongs to a different seam, so it is only shown for connectors that have one. */
  syncStatus?: WooCommerceSyncStatus | null;
  onSync?: () => void;
  onConfigure: () => void;
  onVerify: () => void;
  onUninstall: () => void;
  isSyncing?: boolean;
  isVerifying?: boolean;
}

/**
 * One installed vendor, with its whole lifecycle on the card.
 *
 * Configure / verify / uninstall act on the CONNECTION — the credentials and the provider ports it
 * binds. Sync acts on the catalog pipeline, which is a separate concern with its own state, so it
 * appears only for connectors that feed the catalog and is labelled as its own thing rather than
 * blended into the connection's status.
 */
const InstalledConnectorCard: React.FC<InstalledConnectorCardProps> = ({
  connection,
  label,
  syncStatus,
  onSync,
  onConfigure,
  onVerify,
  onUninstall,
  isSyncing = false,
  isVerifying = false,
}) => {
  const presentation = connectorPresentation(connection.connectorId);
  const Icon = presentation.icon;
  const isHealthy = connection.status === 'connected';
  const primaryConfigKey = Object.keys(connection.config)[0];
  const primaryConfigValue = primaryConfigKey ? connection.config[primaryConfigKey] : undefined;

  return (
    <div className="border border-gray-200 dark:border-border-color rounded-lg p-5 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center shrink-0 ${presentation.bgColor}`}>
            <Icon className={`w-6 h-6 ${presentation.color}`} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold dark:text-white text-gray-900">
                {connection.label || label}
              </h3>
              <span
                className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  isHealthy
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                }`}
              >
                {isHealthy ? 'Installed' : 'Needs attention'}
              </span>
            </div>
            {primaryConfigValue && (
              <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1 mt-0.5 truncate">
                <ExternalLink className="w-3 h-3 shrink-0" />
                {primaryConfigValue}
              </p>
            )}
          </div>
        </div>

        {syncStatus && (
          <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 shrink-0">
            <Clock className="w-4 h-4" />
            {syncStatus.lastSyncStatus === 'running' ? 'Syncing' : 'Idle'}
          </div>
        )}
      </div>

      {connection.statusMessage && (
        <div className="flex gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20">
          <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
          <p className="text-sm text-red-700 dark:text-red-400">{connection.statusMessage}</p>
        </div>
      )}

      {syncStatus && (
        <div className="grid gap-4 sm:grid-cols-3 pt-2 border-t border-gray-100 dark:border-border-color">
          <div>
            <span className="text-xs text-gray-500 dark:text-gray-500">Last catalog sync</span>
            <p className="text-sm dark:text-white text-gray-900">
              {syncStatus.lastSuccessfulSyncAt
                ? new Date(syncStatus.lastSuccessfulSyncAt).toLocaleString()
                : 'Never'}
            </p>
          </div>
          <div>
            <span className="text-xs text-gray-500 dark:text-gray-500">Sync mode</span>
            <p className="text-sm dark:text-white text-gray-900 capitalize">{syncStatus.syncMode}</p>
          </div>
          <div>
            <span className="text-xs text-gray-500 dark:text-gray-500">Synced entities</span>
            <p className="text-sm dark:text-white text-gray-900 capitalize">
              {syncStatus.entities.join(', ')}
            </p>
          </div>
        </div>
      )}

      {syncStatus?.lastSyncError && (
        <div className="flex gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20">
          <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
          <p className="text-sm text-red-700 dark:text-red-400">{syncStatus.lastSyncError}</p>
        </div>
      )}

      <div className="flex flex-wrap justify-end gap-2 pt-2 border-t border-gray-100 dark:border-border-color">
        <Button variant="secondary" size="sm" onClick={onVerify} isLoading={isVerifying}>
          <CheckCircle className="w-4 h-4 mr-2" />
          Test connection
        </Button>
        {onSync && (
          <Button variant="secondary" size="sm" onClick={onSync} isLoading={isSyncing}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Sync now
          </Button>
        )}
        <Button variant="secondary" size="sm" onClick={onConfigure}>
          <Settings className="w-4 h-4 mr-2" />
          Configure
        </Button>
        <Button variant="danger" size="sm" onClick={onUninstall}>
          <Trash2 className="w-4 h-4 mr-2" />
          Remove
        </Button>
      </div>
    </div>
  );
};

export default InstalledConnectorCard;
